import React from 'react';
import styled, { css } from 'styled-components';
import {
  BodyLink,
  Caption,
  CaptionLink,
  Flex,
  mq,
  theme,
  UserIcon
} from '@kvdbil/components';
import { useTranslation } from '~/Locale';
import { formatMoney } from '~/helpers/currency';
import { getMonthlyLoanCost } from '../../helpers';

const Bidder = styled(CaptionLink)<{ isMyBid: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: ${({ theme }) => theme.colors.grayscaleToned.dark1};
`;
const Time = styled(Caption)<{ isMyBid: boolean }>`
  text-align: ${({ isMyBid }) => (isMyBid ? 'left' : 'right')};
`;

const Amount = styled.div<{ isMyBid: boolean; isHighestBid: boolean }>`
  border: 2px solid
    ${({ isMyBid, theme }) =>
      isMyBid ? theme.colors.primary.main : theme.colors.grayscaleToned.dark3};
  color: ${({ theme }) => theme.colors.grayscaleToned.dark3};
  border-radius: 3px;
  padding: 0.75rem 1rem;
  font-size: 1em;
  position: relative;
  text-align: center;
  font-family: ${({ theme }) => theme.typography.fontAlternativeFamily};
  ::before,
  ::after {
    top: 100%;
    left: ${({ isMyBid }) => (isMyBid ? 'auto' : '20px')};
    right: ${({ isMyBid }) => (isMyBid ? '20px' : 'auto')};
    border: solid transparent;
    content: ' ';
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
  }
  ::after {
    border-color: ${({ theme }) => `rgba(${theme.colors.common.white}, 0)`};
    border-top-color: ${({ theme }) => theme.colors.common.white};
    border-width: 5px;
    margin-left: ${({ isMyBid }) => (isMyBid ? 'auto' : '-5px')};
    margin-right: ${({ isMyBid }) => (isMyBid ? '-5px' : 'auto')};

    ${({ isMyBid, isHighestBid }) =>
      isMyBid &&
      isHighestBid &&
      css`
        margin-top: -1px;
      `}
  }
  ::before {
    border-color: rgba(
      ${({ isMyBid, theme }) =>
        isMyBid
          ? theme.colors.primary.main
          : theme.colors.grayscaleToned.dark3},
      0
    );
    border-top-color: ${({ isMyBid, theme }) =>
      isMyBid ? theme.colors.primary.main : theme.colors.grayscaleToned.dark3};
    border-width: 8px;
    margin-left: ${({ isMyBid }) => (isMyBid ? 'auto' : '-8px')};
    margin-right: ${({ isMyBid }) => (isMyBid ? '-8px' : 'auto')};
  }
`;

const AmountContainer = styled.div`
  display: flex;
  height: 100%;
  overflow-y: auto;
  flex-direction: column;
  align-items: space-between;
  justify-content: center;
`;

const AmountLabel = styled.div`
  color: ${({ theme }) => theme.colors.grayscaleToned.dark3};
  font-size: 0.75em;
  font-weight: ${({ theme }) => theme.typography.fontBaseBoldWeight};
  text-transform: uppercase;
  text-align: left;
`;

const AmountValue = styled.div<{ isMyBid: boolean }>`
  font-size: 1.563em;
  color: ${({ theme }) => theme.colors.grayscaleToned.dark3};
  font-weight: 500;
  white-space: nowrap;
  text-align: right;
`;

const ColumnContainer = styled.div<{ isMyBid: boolean }>`
  display: flex;
  overflow-y: auto;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-top: 0.5rem;
  padding-bottom: 1rem;

  ${({ isMyBid }) => {
    if (isMyBid) {
      return css`
        flex-direction: row-reverse;
        > div:first-child {
          text-align: right;
        }
        > div:last-child {
          text-align: left;
        }
      `;
    }
  }}
`;

const Item = styled.li<{ isMyBid: boolean; isHighestBid: boolean }>`
  text-align: left;
  width: 100%;
  max-width: 80%;

  ${mq(null, 'mobileM')} {
    max-width: 85%;
  }

  list-style: none;
  ${({ isHighestBid, theme }) => {
    if (isHighestBid) {
      return css`
        max-width: 100%;
        margin-bottom: 30px;
        border-bottom: 1px solid ${theme.colors.grayscaleToned.light6};
        border-radius: 3px;
        padding: 0;

        ${Amount} {
          padding: 1.5rem 0.75rem;
          line-height: 2em;
          @media screen and (max-width: 420px),
            (min-width: 1024px) and (max-width: 1124px) {
            line-height: 1.563em;
          }
        }
      `;
    }
  }}

  ${({ isMyBid }) => {
    if (isMyBid) {
      return css`
        align-self: end;
      `;
    }
  }}
`;

const Column = styled.div`
  flex: 1;
`;

const StyledCaption = styled(Caption)`
  color: ${theme.colors.grayscaleToned.dark5};
`;

const StyledCaptionLink = styled(CaptionLink)`
  color: ${theme.colors.grayscaleToned.dark5};
`;

export interface BidProps {
  isMyBid: boolean;
  isClosed: boolean;
  isLeading: boolean;
  isHighestBid: boolean;
  amount: number;
  bidderAlias: string;
  typeLabel: string;
  reservationPriceReached: boolean;
  myMaxBid: number;
  interestRate: number;
}

export const Bid = ({
  amount,
  bidderAlias = '',
  typeLabel,
  isHighestBid,
  isMyBid,
  myMaxBid,
  isClosed,
  reservationPriceReached,
  isLeading,
  interestRate
}: BidProps) => {
  const { t } = useTranslation();

  const bidderLabel = isMyBid ? t('%1$s (YOU)', bidderAlias) : bidderAlias;

  const leadingBidLabelSuffix =
    isHighestBid && !isMyBid && myMaxBid === amount ? ` ${t('(not you)')}` : '';

  const monthlyCost = getMonthlyLoanCost({
    amount,
    interestRate
  });

  const monthlyCostString = `${formatMoney({
    value: monthlyCost,
    currency: t('sek/month')
  })}*`;

  return (
    <Item isMyBid={isMyBid} isHighestBid={isHighestBid}>
      <Amount isMyBid={isMyBid} isHighestBid={isHighestBid}>
        {isHighestBid ? (
          <AmountContainer>
            <Flex justify="space-between" align="center">
              {isClosed ? (
                <AmountLabel>
                  {reservationPriceReached
                    ? t('Winning bid')
                    : t('Highest bid')}
                </AmountLabel>
              ) : (
                <AmountLabel>
                  {isLeading ? t('Your leading bid') : t('Leading bid')}
                  {leadingBidLabelSuffix}
                </AmountLabel>
              )}

              <AmountValue isMyBid={isMyBid}>
                {`${formatMoney({
                  value: amount
                })} ${t('SEK')}`}
              </AmountValue>
            </Flex>

            <Flex justify="space-between">
              <StyledCaption>{t('Estimated loan cost')}:</StyledCaption>
              <StyledCaptionLink>{monthlyCostString}</StyledCaptionLink>
            </Flex>

            {Boolean(myMaxBid && myMaxBid > amount) && (
              <Flex justify="space-between">
                <StyledCaption>{t('Your max bid')}:</StyledCaption>
                <StyledCaptionLink>
                  {`${formatMoney({
                    value: myMaxBid
                  })} ${t('SEK')}`}
                </StyledCaptionLink>
              </Flex>
            )}
          </AmountContainer>
        ) : (
          <Flex justify="space-between">
            <BodyLink>
              {formatMoney({
                value: amount
              })}
              {t('SEK')}
            </BodyLink>

            <div>
              <StyledCaption>{t('Estimated loan cost')}:</StyledCaption>
              <StyledCaption style={{ textAlign: 'right' }}>
                {monthlyCostString}
              </StyledCaption>
            </div>
          </Flex>
        )}
      </Amount>
      <ColumnContainer isMyBid={isMyBid}>
        <Bidder isMyBid={isMyBid}>
          <UserIcon fill={theme.colors.grayscaleToned.dark1} />
          {bidderLabel}
        </Bidder>
        <Column>
          <Time isMyBid={isMyBid}>{typeLabel}</Time>
        </Column>
      </ColumnContainer>
    </Item>
  );
};

export default Bid;
